import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { useServiceDetail } from "@/lib/service-detail";
import { formatMoney } from "@/lib/currency";
import { StatCard } from "@/components/v2/StatCard";
import { StatusBadge } from "@/components/v2/StatusBadge";
import { FieldPair } from "@/components/v2/DataTable";
import { EmptyState } from "@/components/v2/EmptyState";
import { parseDateLocal, formatPreferredDate } from "@/lib/booking-date";

/**
 * Overview tab — Pencil `Qp9lm` body. KPI strip, Service Details card,
 * Recent Bookings card. Counts come from the existing list endpoints
 * scoped to this service via `?serviceId=` (added in this phase).
 */
export function ServiceOverview() {
  const { id } = useParams<{ id: string }>();
  const accessToken = useAuthStore((s) => s.accessToken);
  const detail = useServiceDetail(id);
  const service = detail.data?.base;

  const bookings = useQuery({
    queryKey: ["bookings", "service", id],
    enabled: !!accessToken && !!id,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings", {
          params: { query: { serviceId: id, page: 0, size: 5 } },
        }),
      ),
  });

  const clients = useQuery({
    queryKey: ["customers", "service", id],
    enabled: !!accessToken && !!id,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/customers", {
          params: { query: { serviceId: id, page: 0, size: 1 } },
        }),
      ),
  });

  const totalBookings = bookings.data?.totalElements ?? 0;
  const totalClients = clients.data?.totalElements ?? 0;
  const recent = bookings.data?.content ?? [];
  const fieldCount = detail.data?.customFields?.length ?? 0;

  // Revenue MTD: sum of confirmed/paid bookings in current calendar month.
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const revenueMtd = recent.reduce((sum, b) => {
    const pd = (b as { preferredDate?: string | null }).preferredDate;
    const date = pd ? parseDateLocal(pd) : null;
    if (!date || date < monthStart) return sum;
    const price = typeof b.price === "string" ? parseFloat(b.price) : b.price ?? 0;
    return sum + (Number.isFinite(price) ? price : 0);
  }, 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total bookings" value={totalBookings} hint="All-time" />
        <StatCard label="Linked clients" value={totalClients} hint="Distinct" />
        <StatCard
          label="Revenue · MTD"
          value={formatMoney(service?.currency ?? "PHP", revenueMtd, {
            fractionDigits: 0,
          })}
          hint="Last 5 bookings sample"
        />
        <StatCard label="Custom fields" value={fieldCount} hint="Schema fields" />
      </div>

      <section className="rounded-lg border bg-card">
        <div className="px-5 py-4 border-b border-border">
          <p className="text-sm font-semibold tracking-tight">Service details</p>
        </div>
        <div className="px-5 py-4 grid gap-x-8 gap-y-1 md:grid-cols-2">
          <FieldPair label="Service name" value={service?.name ?? "—"} />
          <FieldPair
            label="Status"
            value={
              service ? <StatusBadge label={service.active ? "active" : "draft"} /> : "—"
            }
          />
          <FieldPair label="Custom fields" value={`${fieldCount} configured`} />
          {service?.description && (
            <FieldPair label="Description" value={service.description} />
          )}
        </div>
      </section>

      <section className="rounded-lg border bg-card">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <p className="text-sm font-semibold tracking-tight">Recent bookings</p>
          <Link
            to="../bookings"
            className="text-xs text-primary hover:underline"
          >
            View all →
          </Link>
        </div>
        <div className="divide-y">
          {bookings.isLoading && (
            <p className="px-5 py-6 text-sm text-muted-foreground">Loading…</p>
          )}
          {recent.length === 0 && !bookings.isLoading && (
            <EmptyState
              bare
              title="No bookings yet"
              description="When clients book this service, they'll appear here."
            />
          )}
          {recent.map((b) => (
            <Link
              key={b.id}
              to={`/bookings?focus=${b.id}`}
              className="flex items-center justify-between gap-4 px-5 py-3 text-sm hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">
                  {b.customerName || "(walk-in)"}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {(b as { preferredDate?: string | null }).preferredDate
                    ? formatPreferredDate(
                        (b as { preferredDate?: string | null }).preferredDate!,
                      )
                    : "—"}
                </p>
              </div>
              {b.status && <StatusBadge label={b.status} />}
              <span className="text-sm font-semibold tabular-nums w-24 text-right">
                {formatMoney(b.currency, b.price)}
              </span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
